import { Logger } from 'utilities';
import { INDEXED_DB_TIMEOUT } from './indexedDBStorageManager.constants';
import type {
  IndexedDBPendingFallback,
  IndexedDBStorageManager,
  IndexedDBStorageManagerOptions,
  IndexedDBStorageReadResult,
  IndexedDBStorageRecord,
} from './indexedDBStorageManager.types';
import {
  clearPendingFallback,
  createRemoveFallbackOperation,
  createSetFallbackOperation,
  handleOpenBlocked,
  handleOpenError,
  handleOpenSuccess,
  handleUpgradeNeeded,
  idbRequest,
  idbTransaction,
  readPendingFallback,
  writePendingFallback,
} from './indexedDBStorageManager.utils';

export const createIndexedDBStorageManager = ({
  dbName,
  dbVersion,
  storeName,
  fallbackStorageKey,
  logNamespace,
}: IndexedDBStorageManagerOptions): IndexedDBStorageManager => {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const memoryFallback = new Map<string, IndexedDBPendingFallback>();

  const resetDbPromise = (): void => {
    dbPromise = null;
  };

  const openDB = (): Promise<IDBDatabase> => {
    if (dbPromise) {
      return dbPromise;
    }

    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        resetDbPromise();
        reject(new Error('IndexedDB not available'));
        return;
      }

      const req = indexedDB.open(dbName, dbVersion);

      // If the open request neither resolves nor errors (browser bug, resource pressure,
      // or extension interference), reject so callers do not hang indefinitely.
      let timeoutId: ReturnType<typeof setTimeout>;
      const clearOpenTimeout = () => clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        resetDbPromise();
        reject(new Error(`${logNamespace} IndexedDB open timed out`));
      }, INDEXED_DB_TIMEOUT);

      req.onupgradeneeded = event => handleUpgradeNeeded(event, storeName);
      req.onsuccess = event => handleOpenSuccess(event, resolve, clearOpenTimeout, resetDbPromise);
      req.onerror = () => handleOpenError(req.error, reject, clearOpenTimeout, resetDbPromise);
      req.onblocked = () => handleOpenBlocked(logNamespace);
    });

    return dbPromise;
  };

  const fallbackDeps = {
    memoryFallback,
    fallbackStorageKey,
    logNamespace,
  };

  const persistRecord = async (db: IDBDatabase, key: string, value: unknown): Promise<void> => {
    const tx = db.transaction(storeName, 'readwrite');
    const request = tx.objectStore(storeName).put({
      key,
      value,
      updatedAt: Date.now(),
    } satisfies IndexedDBStorageRecord);

    await Promise.all([idbRequest(request), idbTransaction(tx)]);
  };

  const removeRecord = async (db: IDBDatabase, key: string): Promise<void> => {
    const tx = db.transaction(storeName, 'readwrite');
    const request = tx.objectStore(storeName).delete(key);

    await Promise.all([idbRequest(request), idbTransaction(tx)]);
  };

  const reconcilePendingFallback = async (key: string, pendingFallback: IndexedDBPendingFallback): Promise<void> => {
    try {
      const db = await openDB();
      if (pendingFallback.operation.operation === 'set') {
        await persistRecord(db, key, pendingFallback.operation.value);
      } else {
        await removeRecord(db, key);
      }
      clearPendingFallback({ key, pendingFallback, ...fallbackDeps });
    } catch (error) {
      Logger.debug(`${logNamespace} fallback promotion failed; fallback remains active:`, error);
    }
  };

  const getResult = async <T>(key: string): Promise<IndexedDBStorageReadResult<T>> => {
    const pendingFallback = readPendingFallback<T>({ key, ...fallbackDeps });

    if (pendingFallback) {
      // The fallback is already authoritative. Reconcile IndexedDB without
      // delaying startup when the browser has IndexedDB blocked or stalled.
      reconcilePendingFallback(key, pendingFallback);

      return pendingFallback.operation.operation === 'set'
        ? { status: 'found', value: pendingFallback.operation.value }
        : { status: 'missing' };
    }

    try {
      const db = await openDB();
      const tx = db.transaction(storeName, 'readonly');
      const record = await idbRequest<IndexedDBStorageRecord<T> | undefined>(tx.objectStore(storeName).get(key));
      return record === undefined ? { status: 'missing' } : { status: 'found', value: record.value };
    } catch (error) {
      Logger.debug(`${logNamespace} IndexedDB read failed; using fallback:`, error);
      const fallback = readPendingFallback<T>({ key, ...fallbackDeps });
      if (fallback) {
        return fallback.operation.operation === 'set'
          ? { status: 'found', value: fallback.operation.value }
          : { status: 'missing' };
      }
      return { error, status: 'unavailable' };
    }
  };

  return {
    get: async <T>(key: string) => {
      const result = await getResult<T>(key);
      return result.status === 'found' ? result.value : undefined;
    },
    getResult,
    set: async (key: string, value: unknown) => {
      const pendingFallback = writePendingFallback({
        key,
        operation: createSetFallbackOperation(value),
        ...fallbackDeps,
      });

      try {
        const db = await openDB();
        await persistRecord(db, key, value);
        clearPendingFallback({ key, pendingFallback, ...fallbackDeps });
      } catch (error) {
        Logger.debug(`${logNamespace} IndexedDB write failed; fallback remains active:`, error);
      }
    },
    remove: async (key: string) => {
      const pendingFallback = writePendingFallback({
        key,
        operation: createRemoveFallbackOperation(),
        ...fallbackDeps,
      });

      try {
        const db = await openDB();
        await removeRecord(db, key);
        clearPendingFallback({ key, pendingFallback, ...fallbackDeps });
      } catch (error) {
        Logger.debug(`${logNamespace} IndexedDB removal failed; fallback remains active:`, error);
      }
    },
  };
};
